import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { adminApi } from "../../../../../../../../../../../../services/Api/Admin/adminApi";
import { CrashReportOccurrenceApi } from "../../../../../../../../../../../../services/Api/Admin/adminTypes";
import CrashReportMarkAsPermanentlyBrokenModal from "../../components/CrashReportMarkAsPermanentlyBrokenModal";
import CrashReportOccurrenceBasicInfoCard from "./components/CrashReportOccurrenceBasicInfoCard";

interface MatchParams {
	occurrenceId: string;
}

const CrashReportOccurrencePage = () => {
	const { t } = useTranslation("admin");
	const [occurrence, setOccurrence] = useState<CrashReportOccurrenceApi | undefined>();
	const { addFlash, addSuccessFlash } = useFlash();
	const history = useHistory();
	const { occurrenceId } = useParams<MatchParams>();
	const [showMarkAsPermanentlyBrokenModal, setShowMarkAsPermanentlyBrokenModal] = useState(false);
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		fetchOccurrence();
	}, []);

	const fetchOccurrence = async () => {
		try {
			const params: Record<string, any> = {
				include: "occurrences,full",
			};
			const res = await adminApi.getCrashReportOccurrence(parseInt(occurrenceId), params);
			setOccurrence(res);
			handleChangeTabTitle(
				`#${res.id} |${t("modules.crash_report.field.occurrences.header.title")} | ${t(
					"lib:go_component.navigation.admin"
				)}`
			);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!occurrence) {
		return <LoadingContainer />;
	}

	const handleCloseCrashReport = async () => {
		try {
			await adminApi.closeCrashReport(occurrence.crash_report_id);
			await fetchOccurrence();
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleRestoreCrashReport = async () => {
		try {
			await adminApi.restoreCrashReport(occurrence.crash_report_id);
			await fetchOccurrence();
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleStartFixingCrashReport = async () => {
		try {
			await adminApi.startFixCrashReport(occurrence.crash_report_id);
			await fetchOccurrence();
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const goBack = () => history.push(`/admin/crash_reports/edit/${occurrence.crash_report_id}`);

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.close", { ns: "lib" }),
			action: handleCloseCrashReport,
			variant: "light",
			hide: occurrence?.crash_report?.status === "ARCHIVED",
		},
		{
			title: t("common.action.restore", { ns: "lib" }),
			action: handleRestoreCrashReport,
			variant: "light",
			hide: occurrence?.crash_report?.status === "OCCURRING",
		},
		{
			title: t("modules.crash_report.action.start_fix.title"),
			action: handleStartFixingCrashReport,
			variant: "light",
			hide: !(
				occurrence?.crash_report?.status !== "ARCHIVED" &&
				occurrence?.crash_report?.status !== "WORK_IN_PROGRESS"
			),
		},
		{
			title: t("modules.crash_report.action.mark_as_permanently_broken.title"),
			action: () => setShowMarkAsPermanentlyBrokenModal(true),
			variant: "light",
			hide: occurrence?.crash_report?.status === "PERMANENTLY_BROKEN",
		},
	];

	return (
		<div className="content">
			<Header
				title={`${t("modules.crash_report.field.occurrences.header.title")} #${occurrence.id}`}
				buttons={buttons}
				back={goBack}
			/>
			<div className="row">
				<div className="col-8">
					<CrashReportOccurrenceBasicInfoCard occurrence={occurrence} />
				</div>
			</div>
			{showMarkAsPermanentlyBrokenModal && (
				<CrashReportMarkAsPermanentlyBrokenModal
					isShown={showMarkAsPermanentlyBrokenModal}
					onHide={() => setShowMarkAsPermanentlyBrokenModal(false)}
					include=""
					ids={[occurrence.crash_report_id]}
					handleSuccessAction={fetchOccurrence}
				/>
			)}
		</div>
	);
};

export default CrashReportOccurrencePage;
